import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PaymentsApi from '../../../../apis/PaymentsApi';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import { ExistingAddLicensesSessionDto } from '../../../../generated-types/existingAddLicensesSessionDto';
import { PaymentState } from '../../../../generated-types/paymentState';
import useApi from '../../../../lib/useApi';
import LoadingCircle from '../../../LoadingCircle/LoadingCircle';
import DataRefreshContext from '../../lib/DataRefreshContext';
import AnotherSession from './components/AnotherSession';
import CheckoutSession from './components/CheckoutSession';
import FailedPayment from './components/FailedPayment';
import InitFailed from './components/InitFailed';
import LicensesCount from './components/LicensesCount';
import SuccessfulPayment from './components/SuccessfulPayment';
import WaitingPayment from './components/WaitingPayment';

// Interface for the dialog props
interface AddLicensesDialogProps {
  open: boolean;
  onClose: () => void;
}

// The AddLicensesDialog component
const AddLicensesDialog = ({ open, onClose }: AddLicensesDialogProps) => {
  // APIs and context
  const paymentsApi = useApi(PaymentsApi);
  const snackbar = useContext(SnackbarContext);

  // State variables
  const [numberOfLicenses, setNumberOfLicenses] = useState<number>(1);
  const [initializing, setInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
  const [existingSessionDetails, setExistingSessionDetails] =
    useState<ExistingAddLicensesSessionDto | null>(null);
  const [paymentState, setPaymentState] = useState(PaymentState.NotStarted);
  const [initFailed, setInitFailed] = useState(false);

  // Access the triggerDataRefresh function from the context
  const { triggerDataRefresh } = useContext(DataRefreshContext);

  /**
   * Reset the states of the dialog
   * @param includeExistingSession Set to true to also reset the state of the existing session details
   */
  const resetStates = (includeExistingSession = true) => {
    setPaymentState(PaymentState.NotStarted);
    setClientSecret(null);
    setNumberOfLicenses(1);
    if (includeExistingSession) setExistingSessionDetails(null);
  };

  /**
   * Refresh the payment state variable
   */
  const refreshPaymentState = useCallback(async () => {
    const state = await paymentsApi.getAddLicensesPaymentState();
    setPaymentState(state);

    // If the payment state was successful, refresh all the data so we can get the updated license count
    if (state == PaymentState.Success) triggerDataRefresh();

    return state;
  }, [paymentsApi, triggerDataRefresh]);

  /**
   * Check to see if another manager already has a Stripe session active
   */
  const checkForExistingSession = useCallback(async () => {
    const session = await paymentsApi.checkForExistingAddLicensesSession();
    setExistingSessionDetails(session);
    if (session.foundSession) {
      // Inform the user about the existing session
      snackbar.setAlert(
        <Alert variant="filled" severity="warning">
          An existing checkout session is in already in progress by another
          manager.
        </Alert>
      );
      snackbar.triggerSnackbar();
    }
    return session;
  }, [paymentsApi, snackbar]);

  /**
   * After a payment state was received, this method can be used to reset the state for further
   *  action. For example, after a successful payment, the manager can add more licenses.
   */
  const postPaymentReset = useCallback(async () => {
    setIsLoading(true);
    try {
      // Check for an existing session, needs to occur after resetting the states.
      await checkForExistingSession();

      // We only want to reset the states if we were able to check for an existing session. Also, the previous
      //  call will handle checking for the existing session, so we will ignore reset that here.
      resetStates(false);
    } catch (error) {
      console.error('Error resetting.', error);
      snackbar.setAlert(
        <Alert variant="filled" severity="error">
          An error occurred while resetting the form. If this continues, please
          contact support.
        </Alert>
      );
      snackbar.triggerSnackbar();
    } finally {
      setIsLoading(false);
    }
  }, [checkForExistingSession, snackbar]);

  /**
   * useEffect to initialize the information needed for the dialog
   */
  useEffect(() => {
    if (!open) return; // Only check when dialog is open

    // Setup
    setInitializing(true);
    setInitFailed(false);
    resetStates();

    // The initialize function
    const init = async () => {
      try {
        // Setup stripe
        const publicKey = await paymentsApi.getStripePublicKey();
        if (!publicKey) throw new Error('Stripe public key is empty.');
        setStripePromise(await loadStripe(publicKey));

        // Get the current payment state for the organization
        const state = await refreshPaymentState();

        // Check for an existing session.
        // - We only need to check for this if the session hasn't been started. Otherwise, we don't
        //  care about an existing session. Rather, we'll relay the payment state for the whole
        //  organization.
        if (state == PaymentState.NotStarted) {
          await checkForExistingSession();
        }
      } catch (error: any) {
        setInitFailed(true);
        console.error('Error initializing.', error);
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            Error initializing the add license dialog. If this continues, please
            contact us.
          </Alert>
        );
        snackbar.triggerSnackbar();
      } finally {
        setInitializing(false);
      }
    };

    init();
  }, [
    checkForExistingSession,
    open,
    paymentsApi,
    refreshPaymentState,
    snackbar,
  ]);

  /**
   * Handles creating the checkout session when the user proceeds past adding the licenses.
   */
  const handleCreateCheckoutSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const clientSecret = await paymentsApi.createAddLicensesCheckoutSession({
        numberOfLicenses: numberOfLicenses,
      });
      setClientSecret(clientSecret);
    } catch (error: any) {
      console.error('Failed to create checkout session', error);
      if (error.message.includes('already exists')) {
        snackbar.setAlert(
          <Alert variant="filled" severity="warning">
            A checkout session already exists for your organization.
          </Alert>
        );
      } else if (error.message.includes('Conflict')) {
        snackbar.setAlert(
          <Alert variant="filled" severity="warning">
            A checkout session could not be created at this time. Please try
            again.
          </Alert>
        );
      } else {
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            Failed to proceed. If this continues, please contact us.
          </Alert>
        );
      }
      snackbar.triggerSnackbar();
    } finally {
      setIsLoading(false);
    }
  }, [numberOfLicenses, paymentsApi, snackbar]);

  /**
   * Handles ending the existing checkout session.
   */
  const handleEndSession = useCallback(
    async (owner: string | null = null, showAlert = true) => {
      // Setup the success alert
      const successMessage =
        owner == null
          ? 'Checkout has been reset successfully.'
          : `Existing checkout session for ${owner} has been ended.`;
      const successAlert = (
        <Alert variant="filled" severity="success">
          {successMessage}
        </Alert>
      );

      // Start the resetting
      setIsLoading(true);
      try {
        // Ending with the api will ensure we clear out the session in the backend and Stripe
        await paymentsApi.EndAddLicensesSession();

        // Optionally show a success message alert
        if (showAlert) {
          snackbar.setAlert(successAlert);
          snackbar.triggerSnackbar();
        }

        // If we made it this far, the reset was successful and we can reset the states
        resetStates();
      } catch (error: any) {
        console.error('Failed to end checkout session', error);

        // The checkout session is already being ended on the backend. This indicates that we
        //  hit a condition that caused the distributed lock to conflict
        if (error.message.includes('already ending')) {
          snackbar.setAlert(
            <Alert variant="filled" severity="warning">
              The checkout session is already ending.
            </Alert>
          );
        }
        // The checkout session was already ended, so no action was needed.
        else if (error.message.includes('No active checkout session')) {
          snackbar.setAlert(successAlert);
        }
        // Catch all for other error types
        else {
          snackbar.setAlert(
            <Alert variant="filled" severity="error">
              Failed to end the checkout session.
            </Alert>
          );
        }
        snackbar.triggerSnackbar();
      } finally {
        setIsLoading(false);
      }
    },
    [paymentsApi, snackbar]
  );

  /**
   * Handles closing the dialog and resetting state.
   */
  const handleClose = useCallback(() => {
    onClose();
    resetStates();

    // End the session, if it was left abandoned
    if (paymentState == PaymentState.NotStarted && clientSecret != null)
      handleEndSession(null, false);
  }, [clientSecret, handleEndSession, onClose, paymentState]);

  /**
   * A boolean value to indicate whether the user is actively checking out.
   */
  const isCheckingOut = useMemo(
    () =>
      // Initialization needs to be successful,
      !initFailed &&
      // the stripe promise needs to be set,
      stripePromise != null &&
      // the client secret needs to be set for the checkout session,
      clientSecret != null &&
      // and the payment state needs to be in a not started state
      paymentState == PaymentState.NotStarted,
    [clientSecret, initFailed, paymentState, stripePromise]
  );

  /**
   * This is the content within the dialog, which depends heavily on the current state of the payment
   */
  const dialogContent = useMemo(() => {
    // Don't show anything until we're done initializing or if the dialog is closing
    if (initializing || !open) return <Box height="10em" />;

    // If initialization failed, return early
    if (initFailed) return <InitFailed />;

    // If we have a payment state is success, failed, or waiting, display it
    if (paymentState == PaymentState.Success)
      return <SuccessfulPayment reset={postPaymentReset} />;
    if (paymentState == PaymentState.Failed)
      return <FailedPayment reset={postPaymentReset} />;
    if (paymentState == PaymentState.WaitingPayment)
      return (
        <WaitingPayment
          refresh={() => {
            setIsLoading(true);
            refreshPaymentState().finally(() => setIsLoading(false));
          }}
          isLoading={isLoading}
        />
      );

    // If another manager already has a session, show the warning
    if (existingSessionDetails?.foundSession)
      return (
        <AnotherSession
          closeDialog={onClose}
          endExistingSession={() =>
            handleEndSession(existingSessionDetails.ownerName, true)
          }
          isLoading={isLoading}
          ownerName={existingSessionDetails.ownerName}
        />
      );

    // If the client secret is not yet set, that means the current manager is not yet
    //  checking out and we need to ask them how many licenses they would like to purchase
    if (clientSecret == null)
      return (
        <LicensesCount
          numberOfLicenses={numberOfLicenses}
          setNumberOfLicenses={setNumberOfLicenses}
          handleCreateCheckoutSession={() => {
            setIsLoading(true);
            checkForExistingSession()
              .then((e) => {
                // If we were able to successfully check for an existing
                //  session and no session was found, we can continue
                if (!e.foundSession) handleCreateCheckoutSession();
              })
              .catch((e) => {
                console.error(e);
                snackbar.setAlert(
                  <Alert variant="filled" severity="error">
                    An error occurred while creating the checkout session. If
                    this continues, please contact support.
                  </Alert>
                );
                snackbar.triggerSnackbar();

                // When we handle creating a checkout session, it will handle
                //  turning off the loading circle in the success case.
                setIsLoading(false);
              });
          }}
          isLoading={isLoading}
          isStripePromiseSet={stripePromise != null}
          handleClose={handleClose}
        />
      );

    // The manager is beginning to checkout
    if (isCheckingOut)
      return (
        <CheckoutSession
          stripePromise={stripePromise!}
          clientSecret={clientSecret!}
          setPaymentState={setPaymentState}
        />
      );

    // No matching states...we shouldn't make it this far
    return <></>;
  }, [
    checkForExistingSession,
    clientSecret,
    existingSessionDetails,
    handleClose,
    handleCreateCheckoutSession,
    handleEndSession,
    initFailed,
    initializing,
    isCheckingOut,
    isLoading,
    numberOfLicenses,
    onClose,
    open,
    paymentState,
    postPaymentReset,
    refreshPaymentState,
    snackbar,
    stripePromise,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogContent>
        {/** Header  */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">Add Licenses</Typography>
          <Box display="flex" gap="1em">
            {isCheckingOut && (
              <Tooltip title="Reset the form" arrow>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleEndSession()}
                >
                  Reset
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Close" arrow>
              <IconButton onClick={handleClose}>
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {dialogContent}
        <LoadingCircle
          isLoading={isLoading || initializing}
          loadingDelayMs={0}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddLicensesDialog;
